import { config as appConfig } from '../config'
import { createServiceFetch } from '../utils/validateFetchUrl'

const uiFetch = createServiceFetch(appConfig.api.baseUrl)

// ---------------------------------------------------------------------------
// Types — mirror backend UiCustomizationConfig schema
// ---------------------------------------------------------------------------

export interface StoreCategoryItem {
  id: string
  label: string
  icon?: string | null
  color?: string | null
  gradient?: string | null
}

export interface IndustryItem {
  id: string
  label: string
  color?: string | null
}

export interface OntologyFamilyItem {
  id: string
  label: string
}

export interface DeploymentEnvironmentItem {
  id: string
  label: string
  color?: string | null
}

export interface UiDictionaries {
  store_categories: StoreCategoryItem[]
  industries: IndustryItem[]
  ontology_families: OntologyFamilyItem[]
  deployment_environments: DeploymentEnvironmentItem[]
}

export interface UiBranding {
  company_name: string
  /**
   * Wordmark for the pre-authentication screens (login page, registration and
   * signup modals). Optional: deployments that configure no launch brand omit
   * the key entirely, and a backend predating the field never sends it — both
   * fall back to `company_name`.
   */
  launch_name?: string | null
  company_logo_url: string | null
  /** CSS `background` value (gradient or solid color) for the company-name title. */
  logo_text_gradient: string | null
  theme_name: string | null
  language: string
  supported_languages: string[]
  /** When true, locks the app to the Platform view: hides the mode switch (and, unless `hide_platform_suffix` says otherwise, drops the "Platform" logo suffix). */
  platform_only: boolean
  /**
   * Whether the "Platform" wordmark suffix is hidden, independent of the mode
   * lock. Optional: null/absent (a backend predating the field, or an unset
   * key) inherits `platform_only` — resolve it via isPlatformSuffixHidden().
   */
  hide_platform_suffix?: boolean | null
  /** Platform support contact email displayed on the Home dashboard. */
  support_email: string | null
  /** When false, hides the product documentation link on the login page. */
  show_product_docs: boolean
}

export interface UiCustomization {
  branding: UiBranding
  ui_dictionaries: UiDictionaries
}

// ---------------------------------------------------------------------------
// API call — intentionally unauthenticated (plain fetch, no auth headers)
// ---------------------------------------------------------------------------

/**
 * Failed branding request, carrying the HTTP status so the caller can tell a
 * transient failure (5xx, 408, 429, or a bare network error with no status)
 * from a permanent one (any other 4xx) and decide whether retrying is useful.
 */
export class UiCustomizationRequestError extends Error {
  readonly status: number

  constructor(status: number) {
    super(`Failed to fetch UI customization: ${status}`)
    this.name = 'UiCustomizationRequestError'
    this.status = status
  }
}

export async function fetchUiCustomization(signal?: AbortSignal): Promise<UiCustomization> {
  const baseUrl = appConfig.api.baseUrl.replace(/\/+$/, '')
  const url = `${baseUrl}/v1/customer/branding`
  const response = await uiFetch(url, { signal })
  if (!response.ok) {
    throw new UiCustomizationRequestError(response.status)
  }
  return response.json() as Promise<UiCustomization>
}
